import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Globe, MessageCircle, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';

const HelpScreen: React.FC = () => {
  const navigate = useNavigate();

  const ContactItem = ({
    icon: Icon,
    label,
    value,
    href,
  }: {
    icon: any;
    label: string;
    value: string;
    href?: string;
  }) => (
    <a
      href={href}
      className="flex items-center gap-4 px-4 py-3.5 active:bg-muted transition-colors"
    >
      <div className="w-9 h-9 bg-secondary/10 rounded-lg flex items-center justify-center">
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{value}</p>
      </div>
    </a>
  );

  const faqs = [
    {
      q: 'How do I download a book?',
      a: 'Navigate to the book details page and tap the "Download Book" button. You need to be logged in to download.',
    },
    {
      q: 'Where can I find my downloaded books?',
      a: 'Go to the Library tab in the bottom navigation to view all your downloaded books.',
    },
    {
      q: 'How do I change my password?',
      a: 'Go to Profile > Change Password. You\'ll need your current password to set a new one.',
    },
    {
      q: 'Can I read books offline?',
      a: 'Once a book is downloaded, you can read it anytime. However, you need an internet connection to download new books.',
    },
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="safe-area-top bg-background border-b border-border">
        <div className="flex items-center px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold text-foreground pr-10">
            Help & Support
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 py-6">
          {/* Info Card */}
          <Card className="bg-secondary text-secondary-foreground p-5 mb-6 border-0">
            <div className="flex items-center gap-3 mb-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              <h2 className="text-base font-semibold">Need Help?</h2>
            </div>
            <p className="text-sm text-secondary-foreground/70 leading-relaxed">
              If you have any questions, issues, or feedback about KleverBook, please don't
              hesitate to reach out to us. We're here to help!
            </p>
          </Card>

          {/* Contact Info */}
          <Card className="overflow-hidden mb-6">
            <ContactItem
              icon={Mail}
              label="Email"
              value="support@kleverbook.com"
              href="mailto:support@kleverbook.com"
            />
            <Separator />
            <ContactItem
              icon={Phone}
              label="Phone"
              value="+1 (800) 000-0000"
              href="tel:+18000000000"
            />
            <Separator />
            <ContactItem
              icon={Globe}
              label="Website"
              value="www.kleverbook.com"
            />
            <Separator />
            <ContactItem
              icon={MessageCircle}
              label="Live Chat"
              value="Available 9AM - 6PM EST"
            />
          </Card>

          {/* FAQ Section */}
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Frequently Asked Questions
          </h3>
          <Card className="overflow-hidden">
            <Accordion type="single" collapsible>
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`}>
                  <AccordionTrigger className="px-4 text-sm text-left">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HelpScreen;
